
import React from 'react';
import SalesPerformanceMeter from '../SalesPerformanceMeter';
import { Separator } from '@/components/ui/separator';

interface RevenueSummaryProps {
  metrics: {
    totalAchievement: number;
    totalTarget: number;
    totalRevenueGap: number;
    revenueGapPercentage: string;
    potentialLateStageWinValue: number;
  };
  filterLabel: string;
}

const RevenueSummary: React.FC<RevenueSummaryProps> = ({ 
  metrics,
  filterLabel
}) => {
  const potentialNetRevenue = metrics.totalAchievement + metrics.potentialLateStageWinValue;
  const potentialNetRevenuePercentage = metrics.totalTarget 
    ? (potentialNetRevenue / metrics.totalTarget * 100).toFixed(2) 
    : "00.00";
  const percentageValue = parseFloat(potentialNetRevenuePercentage);
  const textColorClass = percentageValue >= 100 ? 'text-green-400' : 'text-white';

  const projectedGap = potentialNetRevenue - metrics.totalTarget;
  const projectedGapPercentage = metrics.totalTarget 
    ? (projectedGap / metrics.totalTarget * 100).toFixed(2) 
    : "00.00";
  const projectedGapColorClass = projectedGap >= 0 ? 'text-green-400' : 'text-red-400';

  return (
    <div className="w-[50%] bg-secondary/80 p-4 rounded-lg border border-primary/20 transition-all duration-500 hover:border-primary/40 shadow-lg flex flex-col py-[16px]">
      <h3 className="text-primary-light font-medium mb-2 my-0">
        Revenue Gap {filterLabel}
      </h3>
      
      <div className="flex-1 flex flex-col justify-between">
        <div className="h-[230px]">
          <SalesPerformanceMeter achievement={metrics.totalAchievement} target={metrics.totalTarget} />
        </div>
        
        <div className="grid grid-cols-2 gap-2 mb-2 -mt-4 justify-items-center my-[2px]">
          <div className="bg-secondary/60 p-2 rounded-lg border border-primary/80 transition-all duration-500 hover:bg-secondary/70 hover:border-primary shadow hover:shadow-md text-center py-[7px] w-[180px] my-0 px-[6px] mx-0 w-[180px]">
            <div className="text-[14px] text-primary">Revenue Gap Amount</div>
            <div className={`text-lg font-semibold ${metrics.totalRevenueGap > 0 ? 'text-green-400' : 'text-red-400'} animate-fade-in flex justify-center items-center h-[28px]`}>
              {metrics.totalRevenueGap < 0 ? '-$' : '$'}{Math.abs(metrics.totalRevenueGap).toLocaleString()}
            </div>
          </div>
          
          <div className="bg-secondary/60 p-2 rounded-lg border border-pink-500/80 transition-all duration-500 hover:bg-secondary/70 hover:border-pink-500 shadow hover:shadow-md text-center w-[180px] px-px mx-auto w-[180px]">
            <div className="text-[14px] text-primary">Gap Percentage</div>
            <div className={`text-lg font-semibold ${parseFloat(metrics.revenueGapPercentage) > 0 ? 'text-green-400' : 'text-red-400'} animate-fade-in flex justify-center items-center h-[28px]`}>
              {metrics.revenueGapPercentage}%
            </div>
          </div>
        </div>
        
        <Separator className="rounded-none bg-primary-dark my-[17px] py-0" />
        
        <div className="text-[16px] text-primary-light mb-0 pl-0">Revenue Projections</div>
        
        <div className="grid grid-cols-2 gap-0 mt-0 my-0 px-0 mx-[9px] py-0">
          <div className="bg-secondary/60 p-2 rounded-lg border border-amber-500/80 transition-all duration-500 hover:bg-secondary/70 hover:border-amber-500 shadow hover:shadow-md text-center mt-2 mx-[4px] py-[18px] px-[10px] - h-[110px] my-[2px] w-[220px]">
            <div className="text-[13px] text-primary my-0 py-0">Projected revenue (adding potential late stage win revenue)</div>
            <div className={`text-lg font-semibold ${textColorClass} animate-fade-in flex justify-center items-center h-[28px]`}>
              ${Math.round(potentialNetRevenue).toLocaleString()} ({potentialNetRevenuePercentage}%)
            </div>
          </div>
          
          <div className="bg-secondary/60 p-2 rounded-lg border border-emerald-500/80 transition-all duration-500 hover:bg-secondary/70 hover:border-emerald-500 shadow hover:shadow-md text-center mt-2 py-[20px] h-[110px] my-0 w-[220px] px-0 mx-[17px]">
            <div className="text-[13px] text-primary my-0 py-0">Projected Revenue Gap Amount</div>
            <div className="flex flex-col items-center">
              <div className={`text-lg font-semibold ${projectedGapColorClass} animate-fade-in flex justify-center items-center h-[28px]`}>
                {projectedGap < 0 ? '-$' : '$'}{Math.abs(Math.round(projectedGap)).toLocaleString()}
              </div>
              <div className={`text-sm ${projectedGapColorClass}`}>
                ({projectedGap < 0 ? '-' : ''}{Math.abs(parseFloat(projectedGapPercentage)).toFixed(2)}%)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueSummary;
